import { Component, inject, OnInit, signal } from '@angular/core';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzColDirective, NzGridModule } from 'ng-zorro-antd/grid';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzCardModule } from 'ng-zorro-antd/card';
import { VisitAddFacade } from '../../facades/visit-add.facade';
import { ClassificationCodeService, EhrService, EhrTemplateService, User } from '@beautyapp/core';
import { catchError, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PatientAnamnesisComponent } from '../patient-anamnesis/patient-anamnesis.component';
import { ActivateEhrComponent } from '../activate-ehr/activate-ehr.component';
import { UserAddEditComponent } from '../user-add-edit/user-add-edit.component';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { EhrAddNcspComponent } from '../ehr-add-ncsp/ehr-add-ncsp.component';
import { TemplateContentModalComponent } from '../template-content-modal/template-content-modal.component';

@UntilDestroy()
@Component({
  selector: 'beautyapp-ehr-manage',
  imports: [
    NzCardModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormDirective,
    NzButtonComponent,
    NzColDirective,
    NzDividerComponent,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzGridModule,
    NzSelectComponent,
    NzOptionComponent,
    NzCollapseModule,
    NzIconDirective,
    NzTableModule,
    NzSpaceComponent,
    NzSpaceItemDirective,
    NzModalModule,
    NzTreeSelectModule,
    NzAlertComponent,
    TranslateModule,
    PatientAnamnesisComponent,
    NzListModule,
    NzTypographyModule,
    NzPopconfirmModule

  ],
  templateUrl: './ehr-manage.component.html',
  styleUrl: './ehr-manage.component.scss'
})
export class EhrManageComponent implements OnInit {
  visitAddFacade = inject(VisitAddFacade);
  ehrService = inject(EhrService);
  ehrTemplateService = inject(EhrTemplateService);
  modalRef = inject(NzDrawerRef);
  modalService = inject(NzModalService);
  translateService = inject(TranslateService);
  classificationCodeService = inject(ClassificationCodeService);

  modalData = inject(NZ_DRAWER_DATA) as {
    visitId: number | undefined;
    userId: string | undefined;
    user: User | undefined;
  };

  visitId: number | undefined = this.modalData.visitId;
  userId: string | undefined = this.modalData.userId;

  ehrNo = signal<string | null>(null);
  ehrGenerateError = signal<string | null>(null);
  needToAddClinic = signal<boolean>(false);
  submitLoading = signal<boolean>(false);

  ncspCodeMaps = new Map<number, any>();
  ncspCodes = signal<any[]>([]);

  procedures = signal<{
    label: string;
    value: string;
  }[]>([]);

  templates = signal<any[]>([]);

  immunizationTypes = this.classificationCodeService.getImmunizationTypes();
  bloodComponents = this.classificationCodeService.getBloodComponents();


  form: FormGroup = new FormGroup({
    ehrNo: new FormControl([], { validators: [Validators.required], nonNullable: true }),
    procedures: new FormControl([], { validators: [Validators.required], nonNullable: true }),
    userId: new FormControl<string | undefined>(this.userId, [Validators.required]),
    caseNo: new FormControl(''),
    employeeId: new FormControl<string | null>(null, [Validators.required]),
    visitId: new FormControl<number | undefined>(this.visitId, [Validators.required]),
    icd10Id: new FormControl<string | null>(null),
    ncspIds: new FormControl<number[]>([], [Validators.required]),
    templateId: new FormControl<string | null>(null),
    diseaseComplications: new FormArray([]),
    accompanyingDisease: new FormArray([]),
    anamnesisVitae: new FormGroup({
      disabilityStatus: new FormControl<string | null>(null),
      diseaseHistory: new FormArray([]),
      surgicalInterventions: new FormArray([]),
      chronicalDiseases: new FormArray([]),
      transfusions: new FormArray([]),
      alergiesDrugs: new FormArray([]),
      otherAllergiesAndReactions: new FormArray([]),
      immunization: new FormArray([]),
      screening: new FormArray([])
    })
  });

  readonly customStyle = {
    background: '#ffffff',
    'border-radius': '16px',
    'margin-bottom': '16px',
    border: '1px solid #d9d9d9'
  };

  bloodGroup = this.classificationCodeService.getBloodGroup();
  illnessTypes = this.classificationCodeService.getIllness();

  get diseaseComplications() {
    return this.form.get('diseaseComplications') as FormArray;
  }

  get accompanyingDiseaseForm() {
    return this.form.get('accompanyingDisease') as FormArray;
  }

  disabilityStatus: any[] = [
    {
      value: 1,
      alias: 'ზომიერი'
    },
    {
      value: 2,
      alias: 'სუსტი'
    },
    {
      value: 3,
      alias: 'მკვეთრი'
    }
  ];

  ngOnInit() {
    this.loadTemplates();
    this.generateEhrNo();

    // Load ICD codes if not already loaded
    if (this.classificationCodeService.classificationICDCodes().length === 0) {
      this.classificationCodeService.getIcdCodes();
    }
    this.form.get('employeeId')?.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this),
        switchMap((employeeId) => {
          if (employeeId) {
            return of(this.visitAddFacade.getEmployeeProcedures(employeeId));
          } else {
            return of([]);
          }
        })
      )
      .subscribe((procedures) => {
        this.procedures.set(procedures.map(booking => ({
          label: booking.name,
          value: booking.id
        })));
      });

    this.form.get('procedures')?.valueChanges
      .pipe(
        untilDestroyed(this),
        switchMap((serviceIds) => {
          console.log('Service IDs:', serviceIds);
          if (serviceIds) {
            return this.getServiceNcspByServiceId(serviceIds);
          } else {
            return of(null);
          }
        })
      )
      .subscribe((res) => {
        console.log(res);

        res?.ncsps.forEach((ncsp: any) => {
          this.ncspCodeMaps.set(ncsp.id, ncsp);
        });
        this.ncspCodes.set(Array.from(this.ncspCodeMaps.values()));
        const ncspIds = this.ncspCodes().map((m: any) => m.id);
        this.form.get('ncspIds')?.setValue(ncspIds);
      });

    this.form.get('templateId')?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((templateId) => {
        if (templateId) {
          this.applyTemplate(templateId);
        }
      });
  }

  loadTemplates() {
    this.ehrTemplateService.list()
      .pipe(
        untilDestroyed(this),
        catchError(() => of({ data: [] }))
      )
      .subscribe((response) => {
        this.templates.set(response.data);
      });
  }

  applyTemplate(templateId: number) {
    const template = this.templates().find(t => t.id === templateId);
    if (!template || !template.content) return;

    const content = template.content;

    // Apply template content to form (you can customize which fields to apply)
    // Note: Only apply fields that exist in this form
    // The full template application happens in ehr-template-form component

    // You can optionally patch some basic fields here if needed
    console.log('Template selected:', template);
  }

  onSubmit() {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;
    this.submitLoading.set(true);
    this.ehrService.create(this.form.value)
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(res => {
        this.submitLoading.set(false);
        this.modalRef.close();
      });
  }

  generateEhrNo() {
    if (!this.userId) return;
    this.ehrGenerateError.set(null);
    this.ehrService.generateEhr(this.userId)
      .pipe(
        untilDestroyed(this),
        catchError((res) => {
          console.log(res);
          this.ehrGenerateError.set(res.error.message);
          this.needToAddClinic.set(res.error.code === 'CLINIC_NOT_FOUND');
          return of(null);
        })
      )
      .subscribe(res => {
        if (res.ehrNo) {
          this.ehrNo.set(res.ehrNo);
          this.needToAddClinic.set(false);
          this.ehrGenerateError.set(null);
          this.form.get('ehrNo')?.setValue(this.ehrNo());
          console.log(res);
        }
      });
  }

  addOrEdit() {
    this.modalService.create({
      nzTitle: this.translateService.instant('users.addUser/edit'),
      nzContent: UserAddEditComponent,
      nzWidth: '800px',
      nzData: {
        user: this.visitAddFacade.user(),
        isAnketa: true
      },
      nzOnOk: () => {
        this.generateEhrNo();
      }
    }).afterClose.pipe(
      untilDestroyed(this)
    ).subscribe((res) => {
        if (res) {
          this.generateEhrNo();
        }
      }
    );
  }

  activateEhr() {
    this.modalService.create({
      nzTitle: 'EHR-ის გააქტიურება',
      nzContent: ActivateEhrComponent,
      nzWidth: '600px',
      nzOnOk: () => {
        this.generateEhrNo();
      }
    });
  }

  getServiceNcspByServiceId(serviceIds: string[]) {
    console.log('Fetching NCSP for service IDs:', serviceIds);
    return this.classificationCodeService.getServicesToNcsp(serviceIds)
      .pipe(
        untilDestroyed(this),
        tap((res) => {
          console.log(res);
        }),
        catchError((err) => {
          console.error(err);
          return of(null);
        })
      );
  }

  addNCSP() {
    this.modalService.create({
      nzTitle: this.translateService.instant('ehr.addNCSP'),
      nzContent: EhrAddNcspComponent,
      nzWidth: '600px',
      // We'll handle the data in afterClose instead of nzOnOk
      nzFooter: null
    }).afterClose.pipe(
      untilDestroyed(this)
    ).subscribe((result) => {
      if (result && result.items) {
        // Process the selected items returned from the modal
        const selectedNCSPs = result.items;
        selectedNCSPs.forEach((ncsp: any) => {
          this.ncspCodeMaps.set(ncsp.id, ncsp);
        });
        console.log('Selected NCSPs:', this.ncspCodeMaps.values());
        this.ncspCodes.set(Array.from(this.ncspCodeMaps.values()));
        const ncspIds = this.ncspCodes().map((m: any) => m.id);
        this.form.get('ncspIds')?.setValue(ncspIds);
      }
    });
  }

  deleteNCSP(ncsp: any) {
    // Use the key property for consistency with addNCSP
    const keyToDelete = ncsp.key || ncsp.id;
    this.ncspCodeMaps.delete(keyToDelete);
    this.ncspCodes.set(Array.from(this.ncspCodeMaps.values()));
    const ncspIds = this.ncspCodes().map((m: any) => m.id);
    this.form.get('ncspIds')?.setValue(ncspIds);
  }

  viewTemplateContent() {
    const templateId = this.form.get('templateId')?.value;
    if (!templateId) return;

    const template = this.templates().find(t => t.id === templateId);
    if (!template) return;

    this.modalService.create({
      nzTitle: template.name,
      nzContent: TemplateContentModalComponent,
      nzWidth: '80%',
      nzData: {
        template: template
      },
      nzFooter: null
    });
  }
}
